import { useCallback, useEffect, useState } from 'react';
import { agentApi, type ConferenceParticipant, type TransferTarget } from '@/shared/api/agentApi';
import { toast } from '@/components/ui/Toast';
import { Icon } from '@/components/ui/Icon';

// KLIENTO palette (matches the softphone).
const K_ACCENT = '#2DACDF';
const K_ACCENT_HOVER = '#2691BB';
const K_ACCENT_50 = '#F0F9FD';
const K_ACCENT_100 = '#E0F3FA';
const K_INK = '#16242E';
const K_SLATE = '#5C6B75';
const K_LINE = '#E6ECEF';
const K_MIST = '#F7FAFB';
const K_MONO = "'JetBrains Mono', var(--font-mono, ui-monospace), monospace";

/**
 * Conference control, shown ABOVE the softphone during an active call. Lets the
 * agent add UNLIMITED participants — another colleague (agent picker) OR an
 * external phone number (customer/supplier) — and shows the live roster of
 * everyone on the call. Any number of parties join the same mixing bridge.
 */
export function ConferencePanel({ callId }: { callId: number }) {
  const [roster, setRoster] = useState<ConferenceParticipant[]>([]);
  const [targets, setTargets] = useState<TransferTarget[]>([]);
  const [agentId, setAgentId] = useState<string>('');
  const [extNumber, setExtNumber] = useState('');
  const [busy, setBusy] = useState(false);

  const loadRoster = useCallback(async () => {
    try { setRoster(await agentApi.callParticipants(callId)); } catch { /* keep last */ }
  }, [callId]);

  // Load the colleague picker once, and poll the roster while the panel is open.
  useEffect(() => {
    void agentApi.transferTargets().then(setTargets).catch(() => setTargets([]));
  }, []);
  useEffect(() => {
    void loadRoster();
    const h = window.setInterval(loadRoster, 4000);
    return () => window.clearInterval(h);
  }, [loadRoster]);

  async function addAgent() {
    if (!agentId || busy) return;
    setBusy(true);
    try {
      await agentApi.conferenceCall(callId, Number(agentId));
      toast('הנציג מצורף לשיחה', 'ok');
      setAgentId('');
      setTimeout(loadRoster, 1500);
    } catch (e) {
      toast(apiMessage(e) ?? 'שגיאה בצירוף הנציג', 'danger');
    } finally { setBusy(false); }
  }

  async function addExternal() {
    const digits = extNumber.replace(/[^0-9+*#]/g, '');
    if (digits.length < 3 || busy) return;
    setBusy(true);
    try {
      await agentApi.conferenceExternal(callId, digits);
      toast('מחייג ומצרף לשיחה…', 'ok');
      setExtNumber('');
      setTimeout(loadRoster, 2500);
    } catch (e) {
      toast(apiMessage(e) ?? 'שגיאה בצירוף המספר', 'danger');
    } finally { setBusy(false); }
  }

  return (
    <div style={{
      background: K_ACCENT_50, border: `1px solid ${K_ACCENT_100}`, borderRadius: 14,
      padding: 12, display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, color: K_INK }}>
        <Icon name="users" size={16} />
        צירוף לוועידה
      </div>

      {/* live roster */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {roster.length === 0 ? (
          <span style={{ fontSize: 12, color: K_SLATE }}>טוען משתתפים…</span>
        ) : roster.map((p) => (
          <div key={`${p.kind}-${p.id}`} style={{
            display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: K_INK,
            background: '#fff', border: `1px solid ${K_LINE}`, borderRadius: 8, padding: '5px 9px',
          }}>
            <span aria-hidden style={{ display: 'inline-flex' }}>
              <Icon name={p.kind === 'external' ? 'phone' : p.kind === 'customer' ? 'user' : 'users'} size={13} />
            </span>
            <span style={{ fontWeight: 600 }}>{p.label}</span>
            {p.detail && <span dir="ltr" style={{ fontFamily: K_MONO, fontSize: 11.5, color: K_SLATE, marginInlineStart: 'auto' }}>{p.detail}</span>}
          </div>
        ))}
      </div>

      {/* add a colleague */}
      <div style={{ display: 'flex', gap: 6 }}>
        <select
          aria-label="בחר נציג לצירוף"
          value={agentId}
          onChange={(e) => setAgentId(e.target.value)}
          style={{ flex: 1, minWidth: 0, fontSize: 12.5, padding: '7px 8px', border: `1px solid ${K_LINE}`, borderRadius: 8, background: '#fff', fontFamily: 'inherit' }}
        >
          <option value="">צרף נציג…</option>
          {targets.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.role}</option>)}
        </select>
        <button type="button" onClick={addAgent} disabled={!agentId || busy} style={addBtn(!agentId || busy)} aria-label="צרף נציג">
          <Icon name="plus" size={16} />
        </button>
      </div>

      {/* add an external number */}
      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="tel"
          dir="ltr"
          inputMode="tel"
          placeholder="מספר חיצוני לצירוף"
          value={extNumber}
          onChange={(e) => setExtNumber(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void addExternal(); }}
          aria-label="מספר חיצוני לצירוף"
          style={{ flex: 1, minWidth: 0, fontSize: 13, fontFamily: K_MONO, padding: '7px 10px', border: `1px solid ${K_LINE}`, borderRadius: 8, background: K_MIST }}
        />
        <button type="button" onClick={addExternal} disabled={extNumber.replace(/[^0-9+*#]/g, '').length < 3 || busy} style={addBtn(extNumber.replace(/[^0-9+*#]/g, '').length < 3 || busy)} aria-label="צרף מספר חיצוני">
          <Icon name="phone" size={16} />
        </button>
      </div>
    </div>
  );
}

function addBtn(disabled: boolean): React.CSSProperties {
  return {
    width: 40, height: 36, borderRadius: 8, border: 'none', flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: disabled ? '#EEF3F5' : K_ACCENT, color: disabled ? '#93A1AB' : '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer', transition: 'background 150ms',
  };
}

function apiMessage(e: unknown): string | undefined {
  return (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
}
